import _ from "lodash";
import React, { useState, memo, useImperativeHandle, useCallback } from "react";
import classes from "./SimilarityIndexComponent.scss";
import classNames from "classnames";
import {
  RefreshArrowOutlined,
  CloseOutlined,
  WarningTriangleFilled,
  InformationOutlined,
} from "@toddle-edu/ds-icons";
import { connect } from "react-redux";
import EULAModal from "./EULAModal";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import I18nHOC from "UIComponents/I18nHOC";
import { compose } from "lib/apolloClient";
import {
  checkForSimilarity,
  cancelCheckSimilarityReport,
  getTurnitinReportViewerLaunchUrl,
  openLinkInNewTab,
  getIsTurnitinEulaRequired,
} from "modules/Services";
import { SIMILARITY_FILE_MIMETYPE, SIMILARITY_ERROR_CODE_MAP } from "Constants";
import { Button, Tooltip, useDSContext } from "@toddle-edu/ds-web";
import { getSettingValue } from "modules/PermissionModule";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getShowSimilarityOnProject } from "Projects/modules/ProjectModules";
import { onEnterOrSpaceKeyDown } from "Utils";

const ARRAY_FOR_SHOW_RECHECK_ON_ERROR = [
  "PROCESSING_ERROR",
  "CANNOT_EXTRACT_TEXT",
  "TOO_LITTLE_TEXT",
  "TOO_MANY_PAGES",
  "FAILED_TO_EXPORT",
  "UNKNOWN_ERROR",
  "CANNOT_REACH_TURNITIN",
];

const getSimilarityIndexLevel = ({ percentage }) => {
  if (percentage > 30) return "high";
  else if (percentage <= 30 && percentage > 15) return "medium";
  else if (percentage <= 15 && percentage >= 0) return "low";
};

const getSimilarityPercentageClass = ({
  percentage,
  showSimilarityInAllSubmission,
  showInAttachmentItem,
  isDSv2,
}) => {
  const similarityIndexLevel = getSimilarityIndexLevel({ percentage });
  const isSimilarityLevelHigh = similarityIndexLevel === "high";
  const isSimilarityLevelMedium = similarityIndexLevel === "medium";
  const isSimilarityLevelLow = similarityIndexLevel === "low";

  const commonClasses = classNames({
    [classes.similarityPercentage]: showSimilarityInAllSubmission,
    [classes.attachmentItemSimilarityPercentage]: showInAttachmentItem,
    [classes.attachmentItemFont]: showInAttachmentItem,
    [classes.highIndex]: isSimilarityLevelHigh,
    [classes.lowIndex]: isSimilarityLevelLow,
    [classes.focusOutline]: true,
  });

  const dsV1Classes = classNames({
    [classes.cardSimilarityPercentage]: !showSimilarityInAllSubmission,
  });

  const dsV2Classes = classNames({
    [classes.highIndexV2]: isSimilarityLevelHigh,
    [classes.mediumIndexV2]: isSimilarityLevelMedium,
    [classes.lowIndexV2]: isSimilarityLevelLow,
    [classes.cardSimilarityPercentageV2]: !showSimilarityInAllSubmission,
    "text-label": true,
  });

  return classNames({
    [commonClasses]: true,
    [dsV1Classes]: !isDSv2,
    [dsV2Classes]: isDSv2,
  });
};

const SimilarityIndexComponent = props => {
  const {
    metadata,
    attachment: oldAttachment,
    showSimilarityInAllSubmission,
    getIsTurnitinEulaRequired,
    t,
    cancelCheckSimilarityReport,
    showSimilarityOnHeader,
    getTurnitinReportViewerLaunchUrl,
    openLinkInNewTab,
    isParent = false,
    isTurnitinEnabled,
    recheckButtonHoverClass,
    setToastMsg,
    showInAttachmentItem,
    disableForLinks,
    customRef,
    sourceFrom,
    buttonType = "inline",
    buttonVariant,
    testId = "appComponents-similarityIndexComponent",
  } = props;

  const { dsVersion } = useDSContext();
  const isDSv2 = dsVersion === "2.0";
  const [showEULA, setShowEULA] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [action, setAction] = useState("");

  const attachment = !_.isEmpty(_.get(oldAttachment, "attachments", []))
    ? _.first(_.get(oldAttachment, "attachments", []))
    : oldAttachment;

  const { id, type, mimeType, similarityReport } = attachment;

  const checkForSimilarity = async params => {
    const { checkForSimilarity } = props;
    const attachmentId = _.get(params, "attachmentId", "");
    setIsLoading(true);
    await checkForSimilarity({ attachmentId: attachmentId || id, metadata });
    setIsLoading(false);
  };

  const getTurnitinReportURL = async () => {
    const { submissionId, status } = similarityReport;
    setIsLoading(true);
    const turnItInUrl = await getTurnitinReportViewerLaunchUrl({
      input: {
        submissionId,
      },
    });

    if (turnItInUrl && _.isEqual(status, "COMPLETE")) {
      openLinkInNewTab({ url: turnItInUrl });
    } else {
      setToastMsg({
        msg: "toastMsgs:report_not_available_text",
        type: "warning",
      });
    }
    setIsLoading(false);
  };

  const onClickCheckForSimilarity = async param => {
    if (param.stopPropagation) {
      param.stopPropagation();
    }
    setAction("CHECK_FOR_SIMILARITY");
    setIsLoading(true);
    const isEulaRequire = await getIsTurnitinEulaRequired();
    setIsLoading(false);

    if (isEulaRequire) {
      setShowEULA(true);
    } else {
      await checkForSimilarity(param);
    }
  };

  useImperativeHandle(customRef, () => ({
    onAutoCheckForSimilarity: param => {
      onClickCheckForSimilarity(param);
    },
  }));

  const onClickSimilarityPercentage = async event => {
    event.stopPropagation();
    setAction("GENERATE_TURNITIN_REPORT_URL");
    setIsLoading(true);
    const isEulaRequire = await getIsTurnitinEulaRequired();
    setIsLoading(false);

    if (isEulaRequire) {
      setShowEULA(true);
    } else {
      await getTurnitinReportURL();
    }
  };

  const handleKeyDownOnSimilarityPercentage = useCallback(e => {
    onEnterOrSpaceKeyDown(e, onClickSimilarityPercentage);
  }, []);

  const getModalOnAgreeAction = () => {
    switch (action) {
      case "CHECK_FOR_SIMILARITY":
        return checkForSimilarity();
      case "GENERATE_TURNITIN_REPORT_URL":
        return getTurnitinReportURL();
    }
  };

  const onClickCancel = async event => {
    event.stopPropagation();
    await cancelCheckSimilarityReport({
      attachmentId: id,
    });
  };

  const getErrorMessage = ({ errorCode }) => {
    const message = SIMILARITY_ERROR_CODE_MAP[errorCode];
    switch (errorCode) {
      case "PROCESSING_ERROR":
        return t("common:failed_with_message", {
          message: "",
        });
      case "UNKNOWN_ERROR":
        return t(message);
      default:
        return t("common:failed_with_message", {
          message: t(message),
        });
    }
  };

  const getShowRecheckButtonOnError = ({ errorCode }) => {
    return _.includes(ARRAY_FOR_SHOW_RECHECK_ON_ERROR, errorCode);
  };

  const getPercentageComponent = () => {
    const { status, overallMatchPercentage, errorCode } = similarityReport;

    const checkingForSimilarityClass = classNames({
      [classes.checkingForSimilarity]: !isDSv2,
      [classes.checkingForSimilarityHeader]: showSimilarityOnHeader,
      [classes.noBorderStyle]: showSimilarityInAllSubmission,
      [classes.attachmentItemFont]: showInAttachmentItem,
      [classes.checkingForSimilarityV2]: isDSv2,
      "text-body": isDSv2,
    });

    switch (status) {
      case "COMPLETE": {
        const similarityPercentageClass = getSimilarityPercentageClass({
          percentage: overallMatchPercentage,
          showSimilarityInAllSubmission,
          isDSv2,
        });
        const similarityPercentageAttachmentItemClass = getSimilarityPercentageClass(
          {
            percentage: overallMatchPercentage,
            showInAttachmentItem: true,
          }
        );
        const percentageDivClass = classNames({
          [classes.percentageDiv]: true,
          [classes.percentageDivHeader]: showSimilarityOnHeader,
          [classes.noBorderStyle]: showSimilarityInAllSubmission || isParent,
          [classes.similarityDivInAttachmentItem]: showInAttachmentItem,
        });
        const similarityIndexClass = classNames({
          [similarityPercentageClass]: !showSimilarityOnHeader,
          [classes.titleBarIndex]: showSimilarityOnHeader,
        });
        const recheckButtonClass = classNames({
          [classes.recheckButton]: true,
          [classes.recheckButtonHeader]:
            showSimilarityOnHeader || showInAttachmentItem,
          [classes.noBorderStyle]: showSimilarityInAllSubmission || isParent,
          [classes.recheckButtonInAttachment]: showInAttachmentItem,
          [recheckButtonHoverClass]: !!recheckButtonHoverClass,
        });
        const percentageInnerDivClass = classNames({
          [classes.percentageInnerDiv]: true,
          [classes.percentageInnerDivHeader]: showSimilarityOnHeader,
        });

        return (
          <div className={percentageDivClass}>
            {showInAttachmentItem ? (
              <div
                className={classes.similarityContainerInAttachment}
                onClick={onClickSimilarityPercentage}
                tabIndex={0}
                onKeyDown={handleKeyDownOnSimilarityPercentage}
              >
                <div className={`${classes.similarityIndex} text-label-4`}>
                  {t("classRoom:similarity_index")}:
                </div>
                <div
                  className={`${similarityPercentageAttachmentItemClass} text-label-4`}
                >
                  {overallMatchPercentage}%
                </div>
              </div>
            ) : (
              <div className={percentageInnerDivClass}>
                <div
                  className={similarityIndexClass}
                  onClick={onClickSimilarityPercentage}
                  tabIndex={0}
                  onKeyDown={handleKeyDownOnSimilarityPercentage}
                >
                  {showSimilarityInAllSubmission
                    ? `${overallMatchPercentage}%`
                    : t("classRoom:percentage_with_similar", {
                        percentage: overallMatchPercentage,
                      })}
                </div>
              </div>
            )}
            {!isParent && (
              <div
                className={`${recheckButtonClass} ${
                  showInAttachmentItem && "text-label-4"
                }`}
                onClick={onClickCheckForSimilarity}
              >
                <Button
                  size={"small"}
                  variant={
                    buttonVariant
                      ? buttonVariant
                      : showSimilarityOnHeader
                      ? "primary"
                      : "inline"
                  }
                  type={buttonType}
                  _legacy_variant={
                    showSimilarityOnHeader ? "primary" : "inline"
                  }
                  icon={
                    <RefreshArrowOutlined
                      size={"xxx-small"}
                      variant={"subtle"}
                    />
                  }
                  testId={`${testId}-similarityIndex-recheck`}
                >
                  <div
                    className={
                      showInAttachmentItem && classes.attachmentItemFont
                    }
                  >
                    {showSimilarityOnHeader
                      ? t("classRoom:recheck_for_similarity")
                      : t("common:recheck")}
                  </div>
                </Button>
              </div>
            )}
          </div>
        );
      }
      case "PROCESSING": {
        const similarityDivClass = classNames({
          [classes.similarityDiv]: true,
          [classes.similarityDivHeader]: showSimilarityOnHeader,
          [classes.similarityDivInColumn]:
            !showInAttachmentItem && showSimilarityInAllSubmission,
          [classes.similarityDivInAttachmentItem]: showInAttachmentItem,
        });
        const cancelButtonClass = classNames({
          [classes.cancelButton]: true,
          [classes.cancelButtonHeader]: showSimilarityOnHeader,
          [classes.noBorderStyle]:
            showSimilarityInAllSubmission || showInAttachmentItem,
        });

        return !isParent ? (
          <div className={similarityDivClass}>
            <div className={checkingForSimilarityClass}>
              {`${t("classRoom:checking_for_similarity")}...`}
            </div>
            <div className={cancelButtonClass} onClick={onClickCancel}>
              <Button
                size={"small"}
                type={buttonType}
                variant={
                  buttonVariant
                    ? buttonVariant
                    : showSimilarityOnHeader
                    ? "destructive"
                    : "inline"
                }
                _legacy_variant={
                  showSimilarityOnHeader ? "destructive" : "inline"
                }
                icon={
                  !showSimilarityOnHeader && (
                    <CloseOutlined size={"xxx-small"} variant={"subtle"} />
                  )
                }
                testId={`${testId}-similarityIndex-cancel`}
              >
                <div
                  className={showInAttachmentItem && classes.attachmentItemFont}
                >
                  {t("common:cancel")}
                </div>
              </Button>
            </div>
          </div>
        ) : null;
      }
      case "ERROR": {
        const showRecheckButton = getShowRecheckButtonOnError({ errorCode });
        const errorContainerDivClass = classNames({
          [classes.errorContainerDiv]: true,
          [classes.errorContainerDivInColumn]:
            !showSimilarityOnHeader && !showInAttachmentItem,
          [classes.errorContainerDivAttachmentItem]: showInAttachmentItem,
        });
        const errorInnerDivClass = classNames({
          [classes.percentageInnerDiv]: true,
          [classes.percentageInnerDivHeader]: showSimilarityOnHeader,
        });
        const errorDivClass = classNames({
          [classes.errorDiv]: !isDSv2,
          [classes.errorDivOnHeader]: showSimilarityOnHeader,
          [classes.errorDivAttachmentItem]: showInAttachmentItem,
          [classes.errorDivV2]: isDSv2,
          "text-body": isDSv2,
        });
        const recheckButtonClass = classNames({
          [classes.recheckButton]: true,
          [classes.recheckVisible]: true,
          [classes.noBorderStyle]: true,
        });

        return !isParent ? (
          <div className={errorContainerDivClass}>
            <div className={errorInnerDivClass}>
              {!showInAttachmentItem && (
                <div className={classes.warningIcon}>
                  <WarningTriangleFilled
                    size={showSimilarityOnHeader ? "x-small" : "xxx-small"}
                    variant={"critical"}
                  />
                </div>
              )}
              <div className={errorDivClass}>
                {showInAttachmentItem ? (
                  <div className={classes.errorContainer}>
                    <div>{t("classRoom:check_failed")}</div>
                    <Tooltip
                      tooltip={getErrorMessage({ errorCode })}
                      placement={"top"}
                    >
                      <InformationOutlined
                        className={classes.errorInfoIcon}
                        variant={"critical"}
                        size={"xxx-small"}
                      />
                    </Tooltip>
                  </div>
                ) : (
                  getErrorMessage({ errorCode })
                )}
              </div>
            </div>
            {showRecheckButton && (
              <div
                className={`${recheckButtonClass} ${
                  showInAttachmentItem && "text-label-4"
                }`}
                onClick={onClickCheckForSimilarity}
              >
                <Button
                  size={"small"}
                  variant={
                    buttonVariant
                      ? buttonVariant
                      : showSimilarityOnHeader
                      ? "primary"
                      : "primary"
                  }
                  type={buttonType}
                  _legacy_variant={
                    showSimilarityOnHeader ? "primary" : "inline"
                  }
                  icon={
                    <RefreshArrowOutlined
                      size={"xxx-small"}
                      variant={"subtle"}
                    />
                  }
                  testId={`${testId}-similarityIndex-recheck`}
                >
                  <div
                    className={
                      showInAttachmentItem && classes.attachmentItemFont
                    }
                  >
                    {showSimilarityOnHeader
                      ? t("classRoom:recheck_for_similarity")
                      : t("common:recheck")}
                  </div>
                </Button>
              </div>
            )}
          </div>
        ) : null;
      }
    }
  };

  const getCheckForSimilarityComponent = () => {
    const checkForSimilarityClass = classNames({
      [classes.checkForSimilarity]: true,
      [classes.checkForSimilarityHeader]: showSimilarityOnHeader,
      [classes.attachmentItemFont]: showInAttachmentItem,
    });
    const updatedButtonVariant = buttonVariant
      ? buttonVariant
      : showSimilarityOnHeader
      ? "primary"
      : "inline";
    return (
      <div
        className={checkForSimilarityClass}
        onClick={onClickCheckForSimilarity}
      >
        <Button
          size={isDSv2 ? "medium" : "small"}
          _legacy_variant={updatedButtonVariant}
          type={buttonType}
          variant="progressive"
          testId={`${testId}-similarityIndex-check`}
        >
          {t("classRoom:check_for_similarity")}
        </Button>
      </div>
    );
  };

  const getIsTurnitinAllowedMimeType = () => {
    // use SourceFrom to add custom logic on what files are allowed for similarity check

    switch (sourceFrom) {
      default: {
        return (
          (type == "FILE" || type == "LINK") &&
          _.includes(SIMILARITY_FILE_MIMETYPE, mimeType)
        );
      }
    }
  };

  const isAttachmentReadyToShowSimilarity = !_.includes(id, "NEW");

  const disableSimilarityConditions = [
    _.isEqual(type, "LINK") && disableForLinks,
    !isAttachmentReadyToShowSimilarity,
    !isTurnitinEnabled,
  ];

  const displaySimilarity = _.every(disableSimilarityConditions, item => !item);

  return displaySimilarity ? (
    getIsTurnitinAllowedMimeType() ? (
      <div className={classes.similarityContainer}>
        {similarityReport
          ? getPercentageComponent()
          : !isParent && getCheckForSimilarityComponent()}
        {showEULA && (
          <EULAModal
            setShowEULA={setShowEULA}
            onClickAgree={getModalOnAgreeAction}
            t={t}
          />
        )}
        {isLoading && <FullScreenLoader />}
      </div>
    ) : showSimilarityInAllSubmission ? (
      "-"
    ) : null
  ) : null;
};

SimilarityIndexComponent.displayName = "SimilarityIndexComponent";

const mapStateToProps = (state, ownProps) => {
  const organizationId = _.get(state, "login.userInfo.org_id", "");

  //Checking Turnitin Enabled for Personal Projects/Community Projects
  const projectGroupType = _.get(ownProps, "projectGroupType", null);
  const projectGroupId = _.get(ownProps, "projectGroupId", null);
  const academicYearId = _.get(state, "platform.academicYearSelected.id", "");

  const showSimilarityOnProject =
    projectGroupId && projectGroupType
      ? getShowSimilarityOnProject({
          projectGroupType,
          projectGroupId,
          organizationId,
          academicYearId,
        })
      : false;

  //Checking Turnitin Enabled for classroom grades
  let isTurnitinEnabledForGrade = false;
  if (!showSimilarityOnProject) {
    const turnitinEnabledGrades = getSettingValue({
      name: "TurnitinEnabledGrades",
      organizationId,
      readFromUserAndOrgLevel: true,
    });
    const selectedGrade = _.get(ownProps, "course.grade.id", "");
    isTurnitinEnabledForGrade = _.includes(
      turnitinEnabledGrades,
      selectedGrade
    );
  }

  const isTurnitinEnabled =
    isTurnitinEnabledForGrade || showSimilarityOnProject;

  return {
    organizationId,
    isTurnitinEnabled,
  };
};

const mapActionCreators = {
  checkForSimilarity,
  getIsTurnitinEulaRequired,
  cancelCheckSimilarityReport,
  getTurnitinReportViewerLaunchUrl,
  openLinkInNewTab,
  setToastMsg,
};

const MemoizedSimilarityIndexComponent = memo(SimilarityIndexComponent);

export default compose(
  I18nHOC({ resource: ["common", "classRoom"] }),
  connect(mapStateToProps, mapActionCreators)
)(MemoizedSimilarityIndexComponent);
